import React, { useState, useEffect } from "react";
import axios from "axios";
import { useSearchParams, useParams } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Gift, Search, Loader2, Star, ArrowRight, Receipt, Layers, ShoppingBag, Trophy, Info, Wallet, Smartphone, Bell, BellOff, Trash2 } from "lucide-react";
import { API } from "@/lib/api";
import { DEFAULT_BRAND_COLOR, DEFAULT_ON_BRAND, brand } from "@/lib/brand";
import { formatRewardSummary, rewardDisplayLine } from "@/lib/rewardDisplay";


const TIER_COLORS = {
  Bronze: { bg: "bg-purple-100", text: "text-purple-700", light: "bg-purple-50 border-purple-200" },
  Silver: { bg: "bg-purple-200", text: "text-purple-700", light: "bg-purple-50 border-purple-200" },
  Gold: { bg: "bg-purple-500", text: "text-white", light: "bg-purple-50 border-purple-200" },
  Platinum: { bg: "bg-purple-500", text: "text-white", light: "bg-purple-100 border-purple-300" },
};
function getTierColor(name = "") {
  return TIER_COLORS[name] || { bg: "bg-purple-100", text: "text-purple-700", light: "bg-purple-50 border-line" };
}

function StampCard({ current, target, brandColor, textColor }) {
  const stamps = Array.from({ length: target }, (_, i) => i < current);
  return (
    <div className="flex flex-wrap gap-2 justify-center py-2">
      {stamps.map((filled, i) => (
        <div key={i} className="w-9 h-9 rounded-full border-2 flex items-center justify-center transition-all"
          style={filled
            ? { backgroundColor: brandColor || DEFAULT_BRAND_COLOR, borderColor: brandColor || DEFAULT_BRAND_COLOR }
            : { borderColor: (brandColor || DEFAULT_BRAND_COLOR) + "44", backgroundColor: "transparent" }}>
          {filled
            ? <Star size={14} className="text-white fill-white" />
            : <span className="text-xs font-mono" style={{ color: (brandColor || DEFAULT_BRAND_COLOR) + "66" }}>{i + 1}</span>}
        </div>
      ))}
    </div>
  );
}

export function WalletButtons({ result, clientId, brandColor }) {
  const [walletLoading, setWalletLoading] = useState(null);
  const [removeLoading, setRemoveLoading] = useState(false);
  const [removed, setRemoved] = useState(false);

  // Direct link for Apple — iOS Safari handles .pkpass natively via <a> href
  const applePassUrl = result?.customer_id && clientId
    ? `${API}/portal/wallet-pass/apple.pkpass?customer_id=${encodeURIComponent(result.customer_id)}&client_id=${encodeURIComponent(clientId)}`
    : null;

  const handleGoogleWallet = async () => {
    setWalletLoading("google");
    try {
      const resp = await axios.post(`${API}/portal/wallet-pass`, {
        platform: "google",
        customer_id: result.customer_id,
        client_id: clientId,
      });
      if (resp.data.save_url) {
        window.open(resp.data.save_url, "_blank");
      } else if (resp.data.message) {
        alert(resp.data.message);
      }
    } catch (err) {
      alert(err.response?.data?.detail || "Google Wallet not available.");
    } finally {
      setWalletLoading(null);
    }
  };

  const handleRemovePass = async () => {
    if (!window.confirm("Remove your loyalty pass from Apple Wallet? You can always re-add it later.")) return;
    setRemoveLoading(true);
    try {
      await axios.post(`${API}/portal/remove-pass?customer_id=${encodeURIComponent(result.customer_id)}&client_id=${encodeURIComponent(clientId)}`);
      setRemoved(true);
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to remove pass.");
    } finally {
      setRemoveLoading(false);
    }
  };

  if (removed) {
    return (
      <div className="bg-white border border-ink/10 rounded-xl p-5 space-y-3" data-testid="wallet-buttons">
        <div className="flex items-center gap-2">
          <Wallet size={14} className="text-ink/50" />
          <p className="text-xs font-semibold text-ink/60 uppercase tracking-widest">Wallet</p>
        </div>
        <div className="flex items-center gap-2 text-sm text-ink/60">
          <Trash2 size={14} className="text-ink/40" />
          <span>Pass removed. You can re-add it anytime.</span>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <a href={applePassUrl} rel="noopener noreferrer" onClick={() => setRemoved(false)}
            className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-ink text-white text-sm font-medium hover:bg-ink-soft transition-colors no-underline"
            data-testid="re-add-apple-wallet">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>
            Re-add to Apple Wallet
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-ink/10 rounded-xl p-5 space-y-3" data-testid="wallet-buttons">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Wallet size={14} className="text-ink/50" />
          <p className="text-xs font-semibold text-ink/60 uppercase tracking-widest">Add to Wallet</p>
        </div>
        <button
          onClick={handleRemovePass}
          disabled={removeLoading}
          className="flex items-center gap-1 text-xs text-ink/40 hover:text-danger transition-colors"
          data-testid="remove-pass-btn"
        >
          {removeLoading ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
          Remove Pass
        </button>
      </div>
      <p className="text-xs text-ink/50">Save your loyalty card to your phone for quick access.</p>
      <div className="grid grid-cols-2 gap-2">
        <a href={applePassUrl} rel="noopener noreferrer"
          className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-ink text-white text-sm font-medium hover:bg-ink-soft transition-colors no-underline"
          data-testid="add-to-apple-wallet">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>
          Apple Wallet
        </a>
        <button onClick={handleGoogleWallet} disabled={walletLoading === "google"}
          className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium border border-ink/10 hover:bg-surface transition-colors disabled:opacity-50"
          style={{ color: brandColor || DEFAULT_BRAND_COLOR }}
          data-testid="add-to-google-wallet">
          {walletLoading === "google" ? <Loader2 size={14} className="animate-spin" /> : (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
          )}
          Google Wallet
        </button>
      </div>
    </div>
  );
}

export default function CustomerPortalPage() {
  const [searchParams] = useSearchParams();
  const { slug } = useParams();
  // Support both /portal?client=xxx (legacy) and /:slug (new)
  const clientId = slug || searchParams.get("client");

  const [clientInfo, setClientInfo] = useState(null);
  const [identifier, setIdentifier] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [infoLoading, setInfoLoading] = useState(!!clientId);
  const [error, setError] = useState("");
  const [pushStatus, setPushStatus] = useState("idle"); // idle | subscribing | subscribed | denied | unsupported

  // Subscribe to FCM push after lookup result
  useEffect(() => {
    if (!result?.customer_id || !clientId) return;
    if (!("serviceWorker" in navigator) || !("Notification" in window)) {
      setPushStatus("unsupported");
      return;
    }

    const subscribePush = async () => {
      try {
        const permission = await Notification.requestPermission();
        if (permission !== "granted") {
          setPushStatus("denied");
          return;
        }
        setPushStatus("subscribing");

        const { requestNotificationPermission } = await import("../lib/firebase");
        const token = await requestNotificationPermission();

        if (token) {
          await axios.post(`${API}/push/subscribe`, {
            token,
            customer_id: result.customer_id,
            vendor_id: clientId,
          });
          setPushStatus("subscribed");
        } else {
          setPushStatus("denied");
        }
      } catch (err) {
        console.warn("FCM subscription failed:", err);
        setPushStatus("denied");
      }
    };

    subscribePush();
  }, [result?.customer_id, clientId]);

  useEffect(() => {
    if (!clientId) return;
    axios.get(`${API}/portal/client-info?client_id=${clientId}`)
      .then(r => setClientInfo(r.data))
      .catch(() => setClientInfo(null))
      .finally(() => setInfoLoading(false));
  }, [clientId]);

  // Auto-lookup when arriving from the join flow (e.g. /:slug?lookup=<email|mobile>)
  const autoLookup = searchParams.get("lookup");
  useEffect(() => {
    if (!autoLookup || !clientId) return;
    axios.get(`${API}/portal/lookup?identifier=${encodeURIComponent(autoLookup)}&client_id=${clientId}`)
      .then(({ data }) => setResult(data))
      .catch(() => {});
  }, [autoLookup, clientId]);

  const handleLookup = async (e) => {
    e.preventDefault();
    if (!identifier.trim()) return;
    setError(""); setResult(null); setLoading(true);
    try {
      const { data } = await axios.get(
        `${API}/portal/lookup?identifier=${encodeURIComponent(identifier.trim())}&client_id=${clientId || ""}`
      );
      setResult(data);
    } catch (err) {
      setError(err.response?.data?.detail || "Could not find your account.");
    } finally {
      setLoading(false);
    }
  };

  const businessName = result?.business_name || clientInfo?.name || "Rewards";
  const rewardLogic = result?.reward_logic || clientInfo?.reward_logic || "spend_based";

  // Branding from either result or clientInfo
  const brandColor = result?.brand_color || clientInfo?.brand_color || null;
  const textColor = result?.text_color || clientInfo?.text_color || null;
  const vendorId = result?.vendor_id || clientInfo?.id || null;
  const hasLogoHeader = result?.has_logo_header || clientInfo?.has_logo_header || false;
  const hasLogoIcon = result?.has_logo_icon || clientInfo?.has_logo_icon || false;
  const cardLayout = result?.card_layout || clientInfo?.card_layout || null;

  const hasBranding = !!brandColor;
  const bg = brandColor || DEFAULT_BRAND_COLOR;
  const fg = textColor || DEFAULT_ON_BRAND;

  // Check card_layout visibility
  const isVisible = (sectionId) => {
    if (!cardLayout) return true;
    const item = cardLayout.find(i => i.id === sectionId);
    return item ? item.visible : true;
  };

  // Get ordered section IDs from card_layout
  const getOrderedSections = () => {
    if (!cardLayout) return ["loyalty_status", "qr_code", "reward_info", "milestones"];
    return cardLayout.filter(i => i.visible).map(i => i.id);
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: hasBranding ? bg + "0a" : undefined }}
      data-testid="customer-portal-page">
      {/* Branded header */}
      <header className="border-b px-4 py-4" style={hasBranding ? { backgroundColor: bg, borderColor: bg } : { backgroundColor: "white", borderColor: brand.color.line }}>
        <div className="max-w-md mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            {hasLogoIcon && vendorId ? (
              <img src={`${API}/vendors/${vendorId}/logo/icon`} alt="" className="w-8 h-8 rounded-lg object-cover" data-testid="portal-logo-icon" />
            ) : (
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: hasBranding ? fg + "22" : brand.color.purple[500] }}>
                <Gift size={16} style={{ color: hasBranding ? fg : "white" }} />
              </div>
            )}
            <div>
              {infoLoading ? <div className="w-32 h-4 bg-purple-50 rounded animate-pulse" /> : (
                <p className="text-sm font-bold" style={{ color: hasBranding ? fg : brand.color.ink }}>{businessName}</p>
              )}
              <p className="text-xs" style={{ color: hasBranding ? fg + "aa" : brand.color.muted }}>Rewards</p>
            </div>
          </div>
          <Badge variant="outline" className="text-xs" style={hasBranding ? { backgroundColor: fg + "22", color: fg, borderColor: fg + "33" } : { backgroundColor: brand.color.purple[50], color: brand.color.purple[700], borderColor: brand.color.purple[200] }}>
            Self-Serve
          </Badge>
        </div>
        {hasLogoHeader && vendorId && isVisible("logo_header") && (
          <div className="max-w-md mx-auto mt-3">
            <img src={`${API}/vendors/${vendorId}/logo/header`} alt="" className="h-8 max-w-[180px] object-contain" data-testid="portal-logo-header"
              style={{ filter: hasBranding ? "brightness(0) invert(1)" : "none", opacity: 0.9 }} />
          </div>
        )}
      </header>

      <main className="flex-1 flex flex-col items-center px-4 py-8">
        <div className="w-full max-w-md space-y-5">

          {/* Lookup Form */}
          {!result && (
            <div className="bg-white rounded-xl border border-ink/10 shadow-sm p-6">
              <div className="text-center mb-6">
                <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3" style={{ backgroundColor: bg + "15" }}>
                  <Star size={22} style={{ color: bg }} />
                </div>
                <h1 className="text-xl font-bold text-ink">Check Your Rewards</h1>
                <p className="text-sm text-ink/50 mt-1">Enter your email or mobile number</p>
              </div>
              <form onSubmit={handleLookup} className="space-y-3">
                <div className="relative">
                  <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink/40" />
                  <Input type="text" placeholder="Email or mobile number" value={identifier} onChange={(e) => setIdentifier(e.target.value)} className="pl-9 h-11 text-sm" data-testid="portal-identifier-input" required />
                </div>
                {error && <p className="text-sm text-danger bg-danger/10 border border-danger/30 rounded-md px-3 py-2" data-testid="portal-error">{error}</p>}
                <Button type="submit" disabled={loading} className="w-full h-11 text-white font-semibold"
                  style={{ backgroundColor: bg, color: fg }}
                  data-testid="portal-lookup-button">
                  {loading ? <Loader2 size={16} className="animate-spin mr-2" /> : <ArrowRight size={16} className="mr-2" />}
                  {loading ? "Searching…" : "Check My Rewards"}
                </Button>
              </form>
              <p className="text-center text-xs text-ink/40 mt-4">
                {clientInfo?.allow_customer_signup !== false && slug ? (
                  <a href={`/${slug}/join`} className="text-ink font-semibold hover:text-purple-700" data-testid="join-link">Not a member? Join now</a>
                ) : (
                  "Not registered? Ask staff at checkout to add you."
                )}
              </p>
            </div>
          )}

          {/* Results */}
          {result && (
            <div className="space-y-4" data-testid="portal-results">
              {/* Greeting — always at top */}
              {isVisible("business_info") && (
                <div className="bg-white rounded-xl border border-ink/10 shadow-sm p-5">
                  <div className="flex items-start gap-4">
                    <div className="flex-1 text-center">
                      <p className="text-xs text-ink/40 uppercase tracking-widest font-semibold mb-1">Welcome back</p>
                      <h2 className="text-2xl font-bold text-ink">{result.customer_name || "Valued Customer"}</h2>
                      <p className="text-sm text-ink/50 mt-0.5">{result.business_name}</p>
                      {rewardLogic === "tier_based" && result.tier && (
                        <div className="mt-2 inline-flex items-center gap-1.5">
                          <Trophy size={14} className={getTierColor(result.tier.current_name).text} />
                          <Badge variant="outline" className={`text-xs ${getTierColor(result.tier.current_name).light}`}>
                            {result.tier.current_name} Member
                          </Badge>
                        </div>
                      )}
                    </div>
                    {isVisible("qr_code") && result.customer_id && (
                      <div className="flex flex-col items-center shrink-0" data-testid="portal-redeem-qr">
                        <img
                          src={`https://api.qrserver.com/v1/create-qr-code/?size=100x100&margin=4&data=${encodeURIComponent(`${window.location.origin}/customers/${result.customer_id}`)}`}
                          alt="Redeem QR"
                          className="w-[88px] h-[88px] rounded-md border border-ink/10"
                        />
                        <p className="text-[10px] text-ink/40 mt-1.5 text-center leading-tight">Staff: scan to<br/>view & redeem</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Layout-ordered sections */}
              {getOrderedSections().map((sectionId) => {
                switch (sectionId) {
                  case "loyalty_status":
                    if (rewardLogic === "tier_based" && result.tier) {
                      return (
                        <div key={sectionId} className="space-y-4">
                          <div className={`rounded-xl border p-5 ${getTierColor(result.tier.current_name).light}`}>
                            <div className="flex items-center justify-between mb-3">
                              <div>
                                <p className="text-xs font-semibold uppercase tracking-widest text-ink/50">Current Tier</p>
                                <div className="flex items-center gap-2 mt-0.5">
                                  <Trophy size={18} className={getTierColor(result.tier.current_name).text} />
                                  <span className="text-xl font-bold text-ink">{result.tier.current_name}</span>
                                </div>
                              </div>
                              <div className="text-right">
                                <p className="text-xs text-ink/50">Lifetime Points</p>
                                <p className="text-2xl font-bold font-mono text-ink" data-testid="portal-total-earned">{result.total_points_earned?.toLocaleString()}</p>
                              </div>
                            </div>
                            {result.tier.current_perks?.length > 0 && (
                              <div className="flex flex-wrap gap-1.5 mt-2">
                                {result.tier.current_perks.map((perk, i) => (
                                  <span key={i} className="px-2 py-0.5 rounded-full bg-white border border-ink/10 text-xs text-ink/80">{perk}</span>
                                ))}
                              </div>
                            )}
                          </div>
                          {result.tier.next_name && (
                            <div className="bg-white border border-ink/10 rounded-xl p-5">
                              <div className="flex justify-between text-xs text-ink/50 mb-2">
                                <span className="font-semibold">{result.tier.current_name}</span>
                                <span className="font-semibold">{result.tier.next_name} <span className="text-ink/40">({result.tier.next_min_points?.toLocaleString()} pts)</span></span>
                              </div>
                              <div className="w-full bg-purple-50 rounded-full h-2.5">
                                <div className="h-2.5 rounded-full transition-all duration-700" style={{ width: `${result.tier.progress_percent || 0}%`, backgroundColor: bg }} data-testid="portal-progress-bar" />
                              </div>
                              <p className="text-xs text-ink/50 mt-1.5 text-center">
                                {result.tier.points_to_next_tier} more points to reach {result.tier.next_name}
                                {(result.tier.next_reward_preview || result.tier.next_reward_value_gbp > 0) && (
                                  <> — unlocks {rewardDisplayLine(result.tier.next_reward_preview || { value_gbp: result.tier.next_reward_value_gbp, reward_type: "cash_value" })}</>
                                )}
                              </p>
                            </div>
                          )}
                          <div className="rounded-xl p-5 text-white" style={{ backgroundColor: bg }}>
                            <p className="text-xs opacity-80 uppercase tracking-widest font-semibold mb-1" style={{ color: fg }}>Current Points Balance</p>
                            <p className="text-4xl font-bold font-mono" style={{ color: fg }} data-testid="portal-balance">{result.point_balance?.toLocaleString()}</p>
                            <p className="text-sm opacity-70 mt-0.5" style={{ color: fg }}>available points</p>
                          </div>
                        </div>
                      );
                    }
                    if (rewardLogic === "order_based" && result.order_info) {
                      return (
                        <div key={sectionId} className="bg-white border border-ink/10 rounded-xl p-5">
                          <div className="flex items-center justify-between mb-3">
                            <div>
                              <div className="flex items-center gap-1.5">
                                <ShoppingBag size={16} style={{ color: bg }} />
                                <p className="text-sm font-semibold text-ink">Stamp Card</p>
                              </div>
                              <p className="text-xs text-ink/50 mt-0.5">{result.order_info.current_count} / {result.order_info.target} stamps</p>
                            </div>
                            <div className="text-right">
                              <p className="text-xs text-ink/40">{result.order_info.remaining} more</p>
                              <p className="text-xs font-semibold" style={{ color: bg }}>for next reward</p>
                            </div>
                          </div>
                          <StampCard current={result.order_info.current_count} target={result.order_info.target} brandColor={bg} textColor={fg} />
                          <div className="mt-3">
                            <div className="w-full bg-purple-50 rounded-full h-1.5">
                              <div className="h-1.5 rounded-full transition-all duration-700"
                                style={{ width: `${(result.order_info.current_count / result.order_info.target) * 100}%`, backgroundColor: bg }}
                                data-testid="portal-progress-bar" />
                            </div>
                          </div>
                        </div>
                      );
                    }
                    if (rewardLogic === "spend_based" || rewardLogic === "item_based") {
                      return (
                        <div key={sectionId} className="rounded-xl p-6 text-white" style={{ backgroundColor: bg }}>
                          <p className="text-xs font-semibold uppercase tracking-widest opacity-80 mb-1" style={{ color: fg }}>Current Balance</p>
                          <p className="text-5xl font-bold font-mono" style={{ color: fg }} data-testid="portal-balance">{result.point_balance?.toLocaleString()}</p>
                          <p className="text-sm opacity-80 mt-0.5" style={{ color: fg }}>loyalty points</p>
                          <div className="mt-5">
                            <div className="flex justify-between text-xs opacity-80 mb-1.5" style={{ color: fg }}>
                              <span>Progress to {rewardDisplayLine(result.next_reward_preview || { value_gbp: result.reward_value_gbp, reward_type: "cash_value" })}</span>
                              <span>{result.progress_percent}%</span>
                            </div>
                            <div className="w-full rounded-full h-2" style={{ backgroundColor: fg + "33" }}>
                              <div className="rounded-full h-2 transition-all duration-700" style={{ width: `${result.progress_percent}%`, backgroundColor: fg }} data-testid="portal-progress-bar" />
                            </div>
                            <p className="text-xs opacity-70 mt-1.5" style={{ color: fg }}>
                              {result.point_balance >= result.threshold
                                ? "Reward ready to claim!"
                                : `${Math.max(0, result.threshold - result.point_balance)} more points for ${rewardDisplayLine(result.next_reward_preview || { value_gbp: result.reward_value_gbp, reward_type: "cash_value" })}`}
                            </p>
                          </div>
                        </div>
                      );
                    }
                    return null;

                  case "milestones":
                    if (rewardLogic === "order_based" && result.order_info?.stamp_rewards?.length > 0) {
                      return (
                        <div key={sectionId} className="bg-white border border-ink/10 rounded-xl p-5">
                          <p className="text-xs font-semibold text-ink/50 uppercase tracking-widest mb-2">Milestones</p>
                          {result.order_info.stamp_rewards.map((sr, i) => {
                            const reached = result.order_info.current_count >= sr.orders_required;
                            const preview = formatRewardSummary(sr);
                            return (
                              <div key={i} className="flex items-center justify-between px-3 py-2 rounded-md text-xs mb-1 gap-2"
                                style={reached ? { backgroundColor: bg + "12", border: `1px solid ${bg}33` } : { backgroundColor: brand.color.surface, border: `1px solid ${brand.color.line}` }}>
                                <div className="flex items-center gap-2 min-w-0">
                                  <div className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0"
                                    style={reached ? { backgroundColor: bg, color: fg } : { backgroundColor: brand.color.line, color: brand.color.muted }}>
                                    {sr.orders_required}
                                  </div>
                                  <span className={`truncate ${reached ? "font-medium" : "text-ink/60"}`} style={reached ? { color: bg } : {}}>
                                    {preview.title !== "Reward" ? preview.title : `${sr.orders_required}-stamp reward`}
                                  </span>
                                </div>
                                <span className="font-medium shrink-0 text-right" style={{ color: reached ? bg : brand.color.muted }}>
                                  {preview.short || preview.summary}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      );
                    }
                    return null;

                  case "reward_info":
                    return (
                      <React.Fragment key={sectionId}>
                        {result.active_rewards?.length > 0 && (
                          <div className="bg-success/10 border border-success/30 rounded-xl p-5">
                            <p className="text-xs font-semibold uppercase tracking-widest text-success mb-3">Active Rewards ({result.active_rewards.length})</p>
                            {result.active_rewards.map((r, i) => {
                              const preview = formatRewardSummary(r);
                              return (
                              <div key={i} className="flex items-center justify-between py-2 border-b border-success/20 last:border-0 gap-2">
                                <div className="min-w-0">
                                  <p className="text-sm font-bold text-success truncate">
                                    {preview.summary}
                                  </p>
                                  <p className="text-xs text-success">
                                    {preview.title !== preview.summary ? `${preview.title} · ` : ""}
                                    Expires: {r.expiry_date?.slice(0, 10) || "N/A"}
                                  </p>
                                  {preview.reward_type === "item_choice" && preview.item_options?.length > 0 && (
                                    <p className="text-[11px] text-success/80 mt-0.5">Options: {preview.item_options.join(", ")}</p>
                                  )}
                                </div>
                                <Badge className="bg-success/15 text-success border-success/40 shrink-0" variant="outline">Ready</Badge>
                              </div>
                              );
                            })}
                            <p className="text-xs text-success mt-3 font-medium">Show this screen to staff to redeem.</p>
                          </div>
                        )}
                        {result.reward_system_info && (
                          <div className="bg-white border border-ink/10 rounded-xl overflow-hidden" data-testid="portal-reward-info">
                            <div className="px-5 py-3 border-b border-ink/5 flex items-center gap-2 bg-surface">
                              <Info size={14} style={{ color: bg }} />
                              <p className="text-xs font-semibold text-ink/80 uppercase tracking-widest">How It Works</p>
                            </div>
                            <div className="px-5 py-4 space-y-3">
                              {result.reward_system_info.description?.split("\n").map((line, i) => (
                                <p key={i} className={`text-sm ${i === 0 ? "font-medium text-ink" : "text-ink/60 pl-1"}`}>{line}</p>
                              ))}
                              <div className="flex items-center gap-2 pt-2 border-t border-ink/5">
                                <Gift size={12} className="text-ink/40" />
                                <p className="text-xs text-ink/50">
                                  Rewards expire after <strong>{result.reward_system_info.reward_expiry_days} days</strong> once issued.
                                </p>
                              </div>
                            </div>
                          </div>
                        )}
                      </React.Fragment>
                    );

                  default:
                    return null;
                }
              })}

              {/* Wallet + Stats — always at bottom */}
              <WalletButtons result={result} clientId={clientId} brandColor={bg} />

              {/* Notification status */}
              <div className="bg-white border border-ink/10 rounded-xl p-4 flex items-center gap-3" data-testid="push-notification-status">
                {pushStatus === "subscribed" ? (
                  <>
                    <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: bg + "15" }}>
                      <Bell size={14} style={{ color: bg }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-ink">Notifications enabled</p>
                      <p className="text-xs text-ink/50">You&apos;ll get updates when you earn stamps or rewards.</p>
                    </div>
                  </>
                ) : pushStatus === "denied" ? (
                  <>
                    <div className="w-8 h-8 rounded-full bg-purple-50 flex items-center justify-center shrink-0">
                      <BellOff size={14} className="text-ink/40" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-ink">Notifications blocked</p>
                      <p className="text-xs text-ink/50">Enable notifications in your browser settings to get reward updates.</p>
                    </div>
                  </>
                ) : pushStatus === "subscribing" ? (
                  <>
                    <Loader2 size={14} className="animate-spin text-ink/40" />
                    <p className="text-xs text-ink/50">Setting up notifications...</p>
                  </>
                ) : null}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="bg-white border border-ink/10 rounded-xl p-4 text-center">
                  <p className="text-xs text-ink/50 uppercase tracking-widest font-semibold mb-1">Total Earned</p>
                  <p className="text-xl font-bold font-mono text-ink">{result.total_points_earned?.toLocaleString()}</p>
                </div>
                <div className="bg-white border border-ink/10 rounded-xl p-4 text-center">
                  <p className="text-xs text-ink/50 uppercase tracking-widest font-semibold mb-1">Redeemed</p>
                  <p className="text-xl font-bold font-mono text-ink">{result.total_points_redeemed?.toLocaleString()}</p>
                </div>
              </div>

              {result.recent_transactions?.length > 0 && (
                <div className="bg-white border border-ink/10 rounded-xl overflow-hidden">
                  <div className="px-5 py-3 border-b border-ink/5 flex items-center gap-2">
                    <Receipt size={14} className="text-ink/40" />
                    <p className="text-xs font-semibold text-ink/60 uppercase tracking-widest">Recent Visits</p>
                  </div>
                  {result.recent_transactions.map((t, i) => (
                    <div key={i} className="flex items-center justify-between px-5 py-3 border-b border-line last:border-0">
                      <div>
                        <p className="text-sm font-medium text-ink">{t.store_location || t.store_id}</p>
                        <p className="text-xs text-ink/40">{t.timestamp?.slice(0, 10)}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-bold" style={{ color: bg }}>+{t.points_earned} pts</p>
                        <p className="text-xs text-ink/40">£{t.total_amount_gbp?.toFixed(2)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <Button variant="outline" className="w-full" onClick={() => { setResult(null); setIdentifier(""); }} data-testid="portal-search-again">
                Search Again
              </Button>
            </div>
          )}
        </div>
      </main>
      <footer className="text-center py-4 text-xs text-ink/40">Powered by Tribe</footer>
    </div>
  );
}
